import { useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Flag } from '@hivebrite/design-system/Flag';

import { useTopicCardContext } from 'hb-react/shared/apps/TopicsIndexV2/contexts/TopicCardContext';
import { useTopicsIndexContext } from 'hb-react/shared/apps/TopicsIndexV2/contexts/TopicsIndexContext';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import useGet from 'hb-react/shared/hooks/useGet';
import onEnterPress from 'hb-react/shared/utils/onEnterPress';

import Member from './Member';
import GoToGroupButton from '../TopicActions/GoToGroupButton';
import JoinButton from '../TopicActions/JoinButton';
import TopicDetail from '../TopicDetail';

export const TopicDetailsModalWithoutContext = ({
  children,
  isConnectedUser,
  topic,
  setDisplayDetailsModal,
}) => {
  const {
    cover_picture_small_url: coverPictureUrl,
    approval_id: approvalId,
    total_followings: totalFollowings,
    restricted_access: restrictedAccess,
    name,
    description,
    id: topicId,
  } = topic;

  const DISPLAY_MEMBERS_COUNT = 4;

  const [displayMembersCount, setDisplayMembersCount] = useState(
    DISPLAY_MEMBERS_COUNT,
  );

  const requestIsPending = !!approvalId;

  const onCloseModal = useCallback(() => {
    setDisplayDetailsModal(false);
  }, [setDisplayDetailsModal]);

  const { followings = [] } =
    useGet(Routes.topics_topic_path(topicId), {
      enabled: isConnectedUser && !restrictedAccess,
    })?.topic || {};

  const shouldDisplaySeeMoreMembers = useMemo(() => {
    if (followings && followings.length - displayMembersCount > 0) {
      return true;
    }

    return false;
  }, [displayMembersCount, followings]);

  const handleDisplayMoreMembers = useCallback(() => {
    setDisplayMembersCount(displayMembersCount + DISPLAY_MEMBERS_COUNT);
  }, [displayMembersCount]);

  return (
    <ModalWithTemplate
      onCloseModal={onCloseModal}
      header={name}
      screenReadersCloseButtonText={s__('Topics|Close details modal')}
    >
      <Box
        width={{ _: '100%', m: '90%' }}
        flexDirection="column"
        data-testid="topics-more-details-modal"
        mb="10px"
      >
        <Box width="100%" flexDirection={{ _: 'column', m: 'row' }}>
          <Box
            overflow="hidden"
            height={{ _: '152px', m: '142px', l: '150px', xl: '161px' }}
            width={{ _: '283px', m: '266px', l: '280px', xl: '302px' }}
            position="relative"
          >
            <ImageWrapper backgroundImage={coverPictureUrl} />
            {restrictedAccess &&
              (requestIsPending ? (
                <Box position="absolute" top={2} left={2}>
                  <Flag
                    data-testid="private-group-label-wrapper"
                    text={s__('Topics|Pending request')}
                  />
                </Box>
              ) : (
                <Box position="absolute" top={2} left={2}>
                  <Flag
                    data-testid="private-group-label-wrapper"
                    text={s__('Topics|Private')}
                  />
                </Box>
              ))}
          </Box>
          <Box width={{ _: '100%', m: '45%' }}>
            <Box
              ml={{ _: '-16px', m: '8px' }}
              mt={{ _: '12px', m: '0' }}
              flexDirection="column"
              width="100%"
            >
              <Box flexDirection="column" width="100%">
                <TopicDetail
                  display="flex"
                  displayNoneIfDetailsEmpty
                  mb="16px"
                  topic={topic}
                />
              </Box>
              {children}
            </Box>
          </Box>
        </Box>
        {isConnectedUser ? (
          description ? (
            <Box width="100%" mt="20px" flexDirection="column">
              <Box font="h3-semibold" as="h3">
                {s__('Topics|About')}
              </Box>
              <Box
                mt="5px"
                css={css`
                  word-break: break-word !important;
                `}
              >
                {description}
              </Box>
            </Box>
          ) : null
        ) : (
          <Box width="100%" mt="20px" flexDirection="column">
            <Box font="h4-semibold" as="h4">
              {s__('Topics|About')}
            </Box>
            <Box
              mt="5px"
              css={css`
                word-break: break-word !important;
              `}
            >
              {description !== null
                ? description
                : s__('Topics|Log in to see more information about this Group')}
            </Box>
          </Box>
        )}
        {!restrictedAccess && followings && followings.length > 0 && (
          <Box width="100%" mt="16px" flexDirection="column">
            <Box font="h4-semibold" as="h4">
              {s__('Topics|Members')}{' '}
              {totalFollowings && `(${totalFollowings})`}
            </Box>
            <Box
              width="100%"
              mt="-7px"
              flexWrap="wrap"
              flexDirection={{ _: 'column', m: 'row' }}
            >
              {followings.slice(0, displayMembersCount).map((member) => (
                <Box
                  key={member.id}
                  mt="24px"
                  mr={{ _: '0', m: '27px' }}
                  flex={{
                    _: '1 0 auto',
                    m: '1 0 calc(50% - 27px)',
                  }}
                >
                  <Member member={member.user} />
                </Box>
              ))}
              {shouldDisplaySeeMoreMembers && (
                <Box
                  onClick={handleDisplayMoreMembers}
                  onKeyPress={onEnterPress(handleDisplayMoreMembers)}
                  css="cursor: pointer; text-decoration: underline;"
                  mt={4}
                  color="primary"
                  fontWeight={1}
                  tabIndex={0}
                >
                  {s__('Topics|See more members')}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </ModalWithTemplate>
  );
};

const TopicDetailsModal = ({ setDisplayDetailsModal }) => {
  const { topic } = useTopicCardContext();
  const { isConnectedUser } = useTopicsIndexContext();

  return (
    <TopicDetailsModalWithoutContext
      isConnectedUser={isConnectedUser}
      topic={topic}
      setDisplayDetailsModal={setDisplayDetailsModal}
    >
      <Box ml="16px" flexDirection="column" width={{ _: '283px', m: '209px' }}>
        {!topic?.closed && <JoinButton />}
        <Box mt="16px">
          <GoToGroupButton />
        </Box>
      </Box>
    </TopicDetailsModalWithoutContext>
  );
};

export default TopicDetailsModal;

const ImageWrapper = styled(Box).withConfig({
  shouldForwardProp: (prop) => !['backgroundImage'].includes(prop),
})`
  background-image: ${({ backgroundImage }) => `url(${backgroundImage})`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  height: 100%;
  width: 100%;
  border-radius: 2px;
`;
